import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Manager, Reference } from 'react-popper';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isString from 'lodash/isString';
import { Icon, Input, OptionShape } from 'xm-spark';

import style from './SearchSelect.module.scss';
import { Menu } from './Menu';

const getOptionLabel = option =>
  isString(option) ? option : option.label || option.value;

const isActive = ref => ref.current === document.activeElement;

const isBlurToElement = (event, element) => {
  // Based on solution from thread: https://github.com/facebook/react/issues/2011
  // Some browsers (IE11) do not correctly set `relatedTarget` when blur is going to another item:
  const relatedTarget =
    event.relatedTarget ||
    event.explicitOriginalTarget ||
    document.activeElement; // IE11

  return relatedTarget && element && element.contains(relatedTarget);
};

const classesShape = PropTypes.shape({
  disabled: PropTypes.string,
  focused: PropTypes.string,
  menuInner: PropTypes.string,
  menuOuter: PropTypes.string,
  optionValue: PropTypes.string,
  row: PropTypes.string,
  searchSelectInputWrapper: PropTypes.string,
  selectToggle: PropTypes.string,
  singleSelect: PropTypes.string,
  valueOptionWrapper: PropTypes.string,
});

/**
 * SearchSelect renders a selection dropdown input.
 */
export default function SearchSelect({
  arrowComponent,
  className,
  classes,
  disabled,
  itemHeight,
  leftComponent,
  menuMaxHeight,
  menuMinHeight,
  noResultsNode,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  onFocus: onFocusProp,
  onInputChange: onInputChangeProp,
  onKeyDown: onKeyDownProp,
  options,
  placeholder,
  popperProps,
  portalNode,
  value,
  searchDisabled,
  searchInputRef,
  showCheckmarks,
  testIdPrefix,
  addOptionNode,
  menuWidthMatchesInputWidth,
  ...otherProps
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchIsDisabled = searchDisabled || !onInputChangeProp;

  const wrapperRef = useRef();
  const inputRef = searchInputRef || useRef();
  const menuRef = useRef();

  const updateSearchValue = query => {
    setSearchValue(query);
    onInputChangeProp(query);
  };

  const withDisabled =
    fn =>
    (...args) =>
      disabled ? false : fn(...args);

  const close = () => {
    setIsFocused(false);
    setIsOpen(false);
    if (searchValue) {
      updateSearchValue('');
    }
    if (isActive(inputRef)) {
      inputRef.current.blur();
    }
  };

  const onValueClick = withDisabled(() => {
    setIsFocused(true);
    setIsOpen(true);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });

  const onChange = withDisabled(item => {
    onChangeProp(item);
    close();
    if (onBlurProp) {
      onBlurProp();
    }
  });

  const onBlur = e => {
    if (
      !isBlurToElement(e, menuRef.current) &&
      !isBlurToElement(e, inputRef.current) &&
      !isActive(inputRef)
    ) {
      close();
      if (onBlurProp) {
        onBlurProp();
      }
    }
  };

  const onFocus = withDisabled(() => {
    setIsFocused(true);
    setIsOpen(true);
    if (onFocusProp) {
      onFocusProp();
    }
  });

  const onKeyDown = withDisabled(event => {
    if (onKeyDownProp) {
      onKeyDownProp(event);
    }
  });

  return (
    <Manager tag={false}>
      <div
        ref={wrapperRef}
        className={classnames(className, style.hover, {
          [classes.disabled]: disabled,
          [style.disabled]: disabled,
          [classes.focused]: isFocused,
          [style.focused]: isFocused,
        })}
        data-testid={`${testIdPrefix}body-searchSelect`}
      >
        <Reference>
          {({ ref }) => (
            <div
              ref={ref}
              className={classnames(
                classes.selectToggle,
                classes.singleSelect || style.singleSelect,
              )}
              onClick={onValueClick}
              data-testid={`${testIdPrefix}toggle-searchSelect`}
            >
              {leftComponent}
              <div
                className={classnames(
                  classes.searchSelectInputWrapper,
                  style.singleSelectInputWrapper,
                )}
              >
                <Input
                  aria-expanded={!!isOpen && !disabled}
                  readOnly={searchIsDisabled} // Note: this allows focus/blur to work correctly
                  inputRef={inputRef}
                  value={searchValue}
                  onChange={e => updateSearchValue(e.target.value)}
                  className={classnames(
                    classes.singleSelectInput,
                    style.singleSelectInput,
                    {
                      [style.noSearch]: searchIsDisabled,
                    },
                  )}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  onKeyDown={onKeyDown}
                  disabled={disabled}
                  data-testid={`${testIdPrefix}input-searchSelect`}
                  role="combobox"
                  {...otherProps}
                />
                {(!isOpen || searchIsDisabled) && !isEmpty(value) ? (
                  <div
                    className={classnames(
                      style.valueOptionWrapper,
                      { [style.onlySelect]: searchIsDisabled },
                      classes.valueOptionWrapper,
                    )}
                  >
                    <span className={style.valueOption}>
                      {getOptionLabel(value)}
                    </span>
                  </div>
                ) : (
                  isEmpty(value) &&
                  !searchValue && (
                    <div
                      className={classnames(
                        style.valueOptionWrapper,
                        classes.valueOptionWrapper,
                      )}
                    >
                      <span className={style.placeholder}>{placeholder}</span>
                    </div>
                  )
                )}
              </div>
              {arrowComponent || (
                <div className={style.dropdownArrow}>
                  <Icon type="icon-caret-down" className={style.arrow} />
                </div>
              )}
            </div>
          )}
        </Reference>
        <Menu
          classes={classes}
          close={close}
          isOpen={!!isOpen && !disabled}
          itemHeight={itemHeight}
          menuMaxHeight={menuMaxHeight}
          menuMinHeight={menuMinHeight}
          menuRef={menuRef}
          wrapperRef={wrapperRef}
          noResultsNode={noResultsNode}
          addOptionNode={addOptionNode}
          onBlur={onBlur}
          onChange={onChange}
          options={options}
          popperProps={popperProps}
          portalNode={portalNode}
          selectedValue={value ? value.value : null}
          showCheckmarks={showCheckmarks}
          menuWidthMatchesInputWidth={menuWidthMatchesInputWidth}
        />
      </div>
    </Manager>
  );
}

SearchSelect.propTypes = {
  /**
   * Override component for the arrow on the right of the dropdown
   */
  arrowComponent: PropTypes.node,
  /**
   * class to apply to wrapper
   */
  className: PropTypes.string,
  /**
   * list of classes to override
   */
  classes: classesShape,
  /**
   * parent of popper
   */
  disabled: PropTypes.bool,
  /**
   * menu item height
   */
  itemHeight: PropTypes.number,
  /**
   * component left of input
   */
  leftComponent: PropTypes.node,
  /**
   * menu max height
   */
  menuMaxHeight: PropTypes.number,
  /**
   * menu min height
   */
  menuMinHeight: PropTypes.number,
  /**
   * no results component
   */
  noResultsNode: PropTypes.node,
  /**
   * function to invoke when the select box loses focus
   */
  onBlur: PropTypes.func,
  /**
   * function to invoke when an item becomes selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * function to invoke when the select box gets focus
   */
  onFocus: PropTypes.func,
  /**
   * function to invoke when the key down on input
   */
  onKeyDown: PropTypes.func,
  /**
   * function to invoke when the search query changes.
   * This should usually be a debounced function that filters the input options in some way.
   * If null, user is not allowed to type in a search query.
   */
  onInputChange: PropTypes.func,
  /**
   * list of options
   */
  options: PropTypes.arrayOf(OptionShape),
  /**
   * placeholder text
   */
  placeholder: PropTypes.node,
  /**
   * props to pass along to popper
   */
  popperProps: PropTypes.shape(),
  /**
   * parent of popper
   */
  portalNode: PropTypes.shape(),
  /**
   * If true, user is not allowed to type in a search query
   */
  searchDisabled: PropTypes.bool,
  /**
   *
   */
  searchInputRef: PropTypes.node,
  /**
   * Show checkmarks in the menu for selected checkmarks
   */
  showCheckmarks: PropTypes.bool,
  /**
   * Value of the input
   */
  value: PropTypes.any,
  /**
   * Prefix to append to data-testid attributes
   */
  testIdPrefix: PropTypes.string,
  /**
   * add custom value to list component
   */
  addOptionNode: PropTypes.node,
  /**
   * adjust width of the menu with the width of input
   */
  menuWidthMatchesInputWidth: PropTypes.bool,
};

SearchSelect.defaultProps = {
  arrowComponent: null,
  className: '',
  classes: {
    disabled: '',
    focused: '',
    menuInner: '',
    menuOuter: '',
    optionValue: '',
    row: '',
    selectToggle: '',
    singleSelect: '',
  },
  disabled: false,
  options: [],
  portalNode: null,
  popperProps: {},
  placeholder: 'Select or type an option',
  // Intentionally undefined instead of null to use the default in `Menu.jsx`:
  leftComponent: null,
  noResultsNode: undefined,
  menuMinHeight: 0,
  menuMaxHeight: 300,
  itemHeight: 35,
  value: null,
  onBlur: null,
  onFocus: null,
  onInputChange: null,
  onKeyDown: null,
  searchDisabled: false,
  searchInputRef: null,
  showCheckmarks: true,
  menuWidthMatchesInputWidth: false,
  testIdPrefix: '',
  addOptionNode: null,
};

SearchSelect.displayName = 'SearchSelect';
